"use client";
import React, { useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import type { FieldValues, FieldPath } from "react-hook-form";
import type { FormItemProps } from "./types";
import { useFormSchemaContext } from "./FormContext";
import { Form as AntForm, FormItemProps as AntFormItemProps } from "antd";
import * as R from "ramda";
import { ArrayFieldController } from "./ArrayFieldController";
// 辅助函数：判断字符串是否为整数
const isNumeric = (str: string) => /^\d+$/.test(str);

// 将 'data.0.name' 转为 ['data', 0, 'name']
const parsePath = (pathStr: string): (string | number)[] => {
  return pathStr
    .split(".")
    .map((part) => (isNumeric(part) ? parseInt(part, 10) : part));
};

// 核心：通过字符串路径获取错误信息
export const getErrorByStringPath = (
  errors: any,
  fieldPath: string,
): string | undefined => {
  const pathArray = parsePath(fieldPath); // ['data', 0, 'name']
  return R.pathOr(undefined, [...pathArray, "message"], errors);
};

const AFormItem = AntForm.Item;

const FormItem = <T extends FieldValues = FieldValues>({
  name,
  label,
  required,
  help,
  extra,
  children,
  as: Component,
  formItemProps,
  fieldParams = [],
  shouldValidateOnChange = true,
  ...restProps
}: FormItemProps<T> & { formItemProps?: AntFormItemProps }) => {
  const {
    control,
    formState: { errors, },
    setValue,
    getValues,
    watch,
  } = useFormContext<T>();
  const formSchemaContext = useFormSchemaContext();
  const schema = formSchemaContext?.schema;
  const isView = formSchemaContext?.isView;
  const isQuery = formSchemaContext?.isQuery;
  // 获取字段错误信息
  const error = name
    ? Array.isArray(name)
      ? name
        .map((n) => getErrorByStringPath(errors, n as FieldPath<T>))
        .filter(Boolean)
      : getErrorByStringPath(errors, name as FieldPath<T>)
    : null;

  const fieldInfo = () => {
    if (required !== undefined)
      return { isRequired: required, isHidden: false };
    if (!name || !schema) return { isRequired: false, isHidden: false };
    const getFieldRequiredAndHidden = (name: string) => {
      const fieldSchema = (schema as any).fields?.[name];
      if (!fieldSchema) return { isRequired: false, isHidden: false };
      const deps = fieldSchema?.deps || [];
      let schemaDesc: any = null;
      if (deps.length > 0) {
        const values = deps
          .map((dep: string) => ({
            [dep]: watch(dep as any),
          }))
          .reduce((acc: any, cur: any) => ({ ...acc, ...cur }), {});
        const allSchemaDesc = schema.describe({ value: values });
        schemaDesc = (allSchemaDesc as any).fields?.[name];
      } else {
        schemaDesc = fieldSchema.describe({});
      }
      const hiddenDeps = schemaDesc?.meta?.hiddenDeps || [];
      const hiddenWatches = hiddenDeps
        .map((dep: string) => ({ [dep]: watch(dep as any) }))
        .reduce((acc: any, cur: any) => ({ ...acc, ...cur }), {});
      const isHidden = schemaDesc?.meta?.checkHidden?.(hiddenWatches);

      return {
        isRequired:
          (schemaDesc as any).meta?.required ||
          schemaDesc.tests?.some((test: any) => test?.name === "required"),
        isHidden,
      };
    };

    if (Array.isArray(name)) {
      if (name.length === 0) return { isRequired: false, isHidden: false };
      return name.map((n) => getFieldRequiredAndHidden(n))[0];
    }
    return getFieldRequiredAndHidden(name);
  };

  const field = fieldInfo();
  if (field.isHidden) return null;

  const renderChildren = ({
    field: { value, onChange, onBlur, ref },
  }: {
    field: {
      value: any;
      onChange: (value: any) => void;
      onBlur: () => void;
      ref: React.Ref<any>;
    };
  }) => {
    const handleChange = (e: any) => {
      const _value = e?.target ? e.target.value : e;
      onChange(_value);
    };
    const isViewProps = isView ? { isView: true } : {};

    const _fieldValues = fieldParams.reduce(
      (acc: any, cur: any) => ({ ...acc, [cur]: watch(cur as any) }),
      {},
    );

    const formFieldValues = fieldParams?.length
      ? { fieldParams: _fieldValues }
      : {};
    return Component
      ? React.createElement(Component, {
        value: value,
        onChange: handleChange,
        onBlur,
        ref,
        ...formFieldValues,
        ...restProps,
        ...isViewProps,
      } as any)
      : React.isValidElement(children)
        ? React.cloneElement(children, {
          value: value,
          onChange: handleChange,
          onBlur,
          ref,
          ...formFieldValues,
          ...(children?.props || {}),
          ...restProps,
          ...isViewProps,
        } as any)
        : children;
  };

  // 使用Controller进行表单控制
  const _error = isView ? null : Array.isArray(error) ? error[0] : error;
  const _required = isView ? false : field.isRequired;

  const _label = label ? <>
    {label}
    {label && _required && <span style={{ color: "red" }}>*</span>}
  </> : undefined;
  return (
    <AFormItem
      label={_label}
      validateStatus={_error ? "error" : "success"}
      help={_error ? (_error as React.ReactNode) || help : help}
      extra={isView ? "" : extra}
      style={{ marginBottom: isQuery ? 0 : (!schema || isView ? 4 : undefined) }}
      {...formItemProps}
    >
      {Array.isArray(name) ? (
        <ArrayFieldController<T>
          names={name}
          control={control}
          setValue={setValue}
          getValues={getValues}
          shouldValidateOnChange={shouldValidateOnChange}
          render={renderChildren as any}
        />
      ) : name != null ? (
        <Controller<T>
          name={name as FieldPath<T>}
          control={control}
          render={renderChildren as any}
        />
      ) : (
        React.isValidElement(children) ? children : null
      )}
    </AFormItem>
  );
};

export default FormItem;
